import {
  Alert,
  AlertDescription,
  AlertIcon,
  AlertTitle,
  Box,
  Button,
  FormControl,
  FormErrorMessage,
  FormHelperText,
  FormLabel,
  HStack,
  Icon,
  Input,
  ModalBody,
  ModalCloseButton,
  ModalFooter,
  ModalHeader,
  Radio,
  RadioGroup,
  Stack,
  Text,
  Textarea,
} from "@chakra-ui/react";
import {useCallback, useState} from "react";
import {DocumentId} from "../../../Types";
import {useValidation, useValidatedPromiseState} from "../../../hooks/validationState";
import {useBannedEmailValidation} from "../../../hooks/useBannedEmailValidation";
import externalApi from "../../../api/external";
import {useSessionState} from "../../../hooks/sessionState";
import {useClientAccount} from "../../hooks/clientAccount";
import {withModal} from "../../../state/withModal";
import {Type} from "../../../utils/typing";
import {q, useQueryData} from "../../../state";
import {PencilSquareIcon} from "@heroicons/react/20/solid";
import RestrictedDocumentSelector from "./RestrictedDocumentSelector";

type RequestDocumentAccessModalProps = {
  onClose: () => void;
  initialSelectedDocumentIds: DocumentId[];
};

const RequestDocumentAccessModalSuccess = ({onClose, nda}: {onClose: () => void; nda: boolean}) => {
  let title = "Access request sent";

  if (nda) {
    title += " - NDA required";
  }

  return (
    <>
      <ModalHeader>{title}</ModalHeader>
      <ModalCloseButton />
      <ModalBody fontSize="md">
        <Stack spacing={4}>
          <Alert status="success">
            <AlertIcon />
            <Box>
              <AlertDescription>Your document access request has been sent successfully.</AlertDescription>
            </Box>
          </Alert>
          {nda ? (
            <p>Access to these documents requires signing an NDA. You should receive emails with any NDAs shortly.</p>
          ) : (
            <p>You will receive the requested documents via email once your request has been approved.</p>
          )}
        </Stack>
      </ModalBody>
      <ModalFooter>
        <Button variant="outline" onClick={onClose}>
          Close
        </Button>
      </ModalFooter>
    </>
  );
};

type RequestDocumentAccessModalFormProps = RequestDocumentAccessModalProps & {
  onSuccess: (data: {nda: boolean}) => void;
};

const RequestDocumentAccessModalForm = ({
  onSuccess,
  onClose,
  initialSelectedDocumentIds,
}: RequestDocumentAccessModalFormProps) => {
  const account = useClientAccount();
  const documents = useQueryData(q.external.trustCenterDocuments(account)).filter(d => !d.is_readable);
  const [selectedDocumentIds, setSelectedDocumentIds] = useState(initialSelectedDocumentIds);

  const requiresEsignature = selectedDocumentIds
    .map(id => documents.find(d => d.document_id === id)!)
    .some(d => d.requires_esignature);

  const [email, setEmail] = useSessionState("requestDocumentAccessModalEmail", Type.String(), "");
  const [name, setName] = useSessionState("requestDocumentAccessModalName", Type.String(), "");
  const [phone, setPhone] = useSessionState("requestDocumentAccessModalPhone", Type.String(), "");
  const [pointOfContact, setPointOfContact] = useSessionState(
    "requestDocumentAccessModalPointOfContact",
    Type.String(),
    "",
  );
  const [message, setMessage] = useSessionState("requestDocumentAccessModalMessage", Type.String(), "");
  const [alreadySignedNda, setAlreadySignedNda] = useSessionState(
    "requestDocumentAccessModalAlreadySignedNda",
    Type.Boolean(),
    false,
  );

  const [validationErrors, setValidationErrors] = useValidation(useState({}), {email, name});
  const [sending, send] = useValidatedPromiseState(
    async () => {
      await externalApi.trustCenters.requestDocumentAccess(account, {
        document_ids: selectedDocumentIds,
        email,
        name,
        phone,
        point_of_contact: pointOfContact,
        message,
        already_signed_nda: alreadySignedNda,
      });

      onSuccess({nda: requiresEsignature && !alreadySignedNda});
    },
    [
      account,
      selectedDocumentIds,
      email,
      name,
      phone,
      pointOfContact,
      message,
      alreadySignedNda,
      onSuccess,
      requiresEsignature,
    ],
    setValidationErrors,
  );

  const isBannedEmails = useBannedEmailValidation(email);

  return (
    <form onSubmit={send}>
      <ModalHeader>Request access to restricted documents</ModalHeader>
      <ModalCloseButton />
      <ModalBody>
        <HStack alignItems="flex-start" spacing={8}>
          <FormControl alignSelf="stretch" display="flex" flexDirection="column" flex="1">
            <FormLabel>1) Select documents</FormLabel>
            <RestrictedDocumentSelector
              documents={documents}
              value={selectedDocumentIds}
              onChange={setSelectedDocumentIds}
            />
          </FormControl>
          <FormControl flex="1">
            <FormLabel>2) Fill out your details</FormLabel>
            <Stack spacing={0} rounded="md">
              {requiresEsignature && (
                <Stack fontSize="md" p={4} px={6} bg="blue.100" rounded="md" color="blue.800" spacing={4} my={4}>
                  <Stack spacing={2}>
                    <HStack spacing={6}>
                      <Icon as={PencilSquareIcon} fontSize="xl" />
                      <Text fontWeight="600">Requires NDA</Text>
                    </HStack>
                    <Text>You must sign an NDA to access some of these documents.</Text>
                  </Stack>
                  <FormControl>
                    <RadioGroup
                      value={alreadySignedNda ? "yes" : "no"}
                      onChange={v => setAlreadySignedNda(v === "yes")}
                    >
                      <Stack spacing={1} fontWeight="500">
                        <Radio value="no" borderColor="blue.800">
                          Send me an NDA by email
                        </Radio>
                        <Radio value="yes" borderColor="blue.800">
                          I have already signed an NDA
                        </Radio>
                      </Stack>
                    </RadioGroup>
                  </FormControl>
                </Stack>
              )}
              <Stack spacing={4} mt={4}>
                <FormControl isRequired isInvalid={isBannedEmails || !!validationErrors?.email?.[0]}>
                  <FormLabel>Your email</FormLabel>
                  <Input value={email} onChange={e => setEmail(e.target.value)} autoFocus />
                  {validationErrors?.email?.[0] && (
                    <FormErrorMessage>{validationErrors.email[0].message}</FormErrorMessage>
                  )}
                  {isBannedEmails && (
                    <FormErrorMessage>
                      This email domain is not allowed. Please use your work email address.
                    </FormErrorMessage>
                  )}
                  <FormHelperText>
                    You will receive the requested documents via email once your request has been approved.
                  </FormHelperText>
                </FormControl>
                <FormControl isRequired>
                  <FormLabel>Your name</FormLabel>
                  <Input value={name} onChange={e => setName(e.target.value)} />
                  {validationErrors.name && validationErrors.name[0] && (
                    <FormErrorMessage>{validationErrors.name[0].message}</FormErrorMessage>
                  )}
                </FormControl>
                <FormControl>
                  <FormLabel>Your phone number</FormLabel>
                  <Input value={phone} onChange={e => setPhone(e.target.value)} />
                </FormControl>
                <FormControl>
                  <FormLabel>Point of Contact at {account.display_name}</FormLabel>
                  <Input value={pointOfContact} onChange={e => setPointOfContact(e.target.value)} />
                </FormControl>
                <FormControl>
                  <FormLabel>Additional information</FormLabel>
                  <Textarea value={message} onChange={e => setMessage(e.target.value)} rows={6} />
                </FormControl>
                {sending.lastError ? (
                  <Alert status="error" rounded="md">
                    <AlertIcon boxSize="40px" />
                    <Box>
                      <AlertTitle fontSize="md">Error sending request</AlertTitle>
                      <AlertDescription fontSize="md">{`${sending.lastError}`}</AlertDescription>
                    </Box>
                  </Alert>
                ) : null}
              </Stack>
            </Stack>
          </FormControl>
        </HStack>
      </ModalBody>
      <ModalFooter>
        <HStack spacing="3">
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button
            type="submit"
            colorScheme="blue"
            isLoading={sending.inProgress}
            isDisabled={sending.inProgress || selectedDocumentIds.length === 0}
          >
            Send request
          </Button>
        </HStack>
      </ModalFooter>
    </form>
  );
};

export const RequestDocumentAccessModal = withModal(
  (props: RequestDocumentAccessModalProps) => {
    type Stage = {type: "form"} | {type: "success"; nda: boolean};

    const [stage, setStage] = useState<Stage>({type: "form"});

    const handleSuccess = useCallback(({nda}: {nda: boolean}) => {
      setStage({type: "success", nda});
    }, []);

    return (
      <>
        {stage.type === "form" && <RequestDocumentAccessModalForm {...props} onSuccess={handleSuccess} />}
        {stage.type === "success" && <RequestDocumentAccessModalSuccess onClose={props.onClose} nda={stage.nda} />}
      </>
    );
  },
  {size: "6xl", closeOnOverlayClick: false},
);
